import { getTutorcloudLaunchUrl } from "../api/auth";

/** Per-teacher marker that this browser already started a TutorCloud return launch. */
export const TUTORCLOUD_HANDOFF_PREFIX = "tema:tc-handoff:";
const TEACHER_ID_STORAGE_KEY = "tema_teacher_id";

let pendingLaunchUrl = null;

function trimEnv(value, fallback = "") {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return fallback;
}

function joinTutorcloudUrl(base, path) {
  const normalizedBase = String(base || "").trim().replace(/\/+$/, "");
  const normalizedPath = String(path || "").trim();
  if (!normalizedBase) {
    return normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  }
  if (!normalizedPath) {
    return normalizedBase;
  }
  const pathPart = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
  return `${normalizedBase}${pathPart}`;
}

export function getTutorcloudFrontendBaseUrl() {
  return trimEnv(
    process.env.REACT_APP_TUTORCLOUD_FRONTEND_URL,
    "http://localhost:3000",
  );
}

export function getTutorcloudBillingUrl() {
  return joinTutorcloudUrl(
    getTutorcloudFrontendBaseUrl(),
    trimEnv(process.env.REACT_APP_TUTORCLOUD_TEACHER_BILLING_PATH, "/teacher/billing"),
  );
}

function isAbsoluteHttpUrl(value) {
  return /^https?:\/\//i.test(value);
}

/** Prefer API subscription URL; fall back to env-built TutorCloud billing page. */
export function resolveTutorcloudBillingUrl(tcBillingUrl) {
  const fromApi = String(tcBillingUrl || "").trim();
  if (!fromApi) {
    return getTutorcloudBillingUrl();
  }
  if (isAbsoluteHttpUrl(fromApi)) {
    return fromApi;
  }
  // Relative paths must not stay on the TEMA origin (e.g. localhost:3030/teacher/billing).
  return joinTutorcloudUrl(getTutorcloudFrontendBaseUrl(), fromApi);
}

export function appendTutorcloudLaunchNext(launchUrl, nextPath) {
  const path = String(nextPath || "").trim();
  if (!path.startsWith("/") || path.startsWith("//")) {
    return launchUrl;
  }
  try {
    const url = new URL(launchUrl, window.location.origin);
    url.searchParams.set("next", path);
    return url.href;
  } catch {
    const joiner = String(launchUrl || "").includes("?") ? "&" : "?";
    return `${launchUrl}${joiner}next=${encodeURIComponent(path)}`;
  }
}

export function billingPathFromTcBillingUrl(tcBillingUrl) {
  const resolved = resolveTutorcloudBillingUrl(tcBillingUrl);
  try {
    const url = new URL(resolved);
    return `${url.pathname}${url.search}`;
  } catch {
    return trimEnv(process.env.REACT_APP_TUTORCLOUD_TEACHER_BILLING_PATH, "/teacher/billing");
  }
}

function readStoredTeacherId() {
  try {
    return sessionStorage.getItem(TEACHER_ID_STORAGE_KEY)?.trim() || "";
  } catch {
    return "";
  }
}

function handoffStorageKey(teacherId = readStoredTeacherId()) {
  const id = String(teacherId || "").trim();
  if (!id) {
    return "";
  }
  return `${TUTORCLOUD_HANDOFF_PREFIX}${id}`;
}

export function hasTutorcloudHandoff(teacherId = readStoredTeacherId()) {
  const key = handoffStorageKey(teacherId);
  if (!key) {
    return false;
  }
  try {
    return sessionStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function markTutorcloudHandoff(teacherId = readStoredTeacherId()) {
  const key = handoffStorageKey(teacherId);
  if (!key) {
    return;
  }
  try {
    sessionStorage.setItem(key, "1");
  } catch {
    // Ignore storage failures in private browsing.
  }
}

function resolveDestinationUrl(path) {
  const raw = String(path || "").trim();
  if (!raw) {
    return "";
  }
  if (isAbsoluteHttpUrl(raw)) {
    return raw;
  }
  if (raw.startsWith("//")) {
    return "";
  }
  return joinTutorcloudUrl(getTutorcloudFrontendBaseUrl(), raw);
}

function loadLaunchUrl() {
  if (!pendingLaunchUrl) {
    pendingLaunchUrl = Promise.resolve()
      .then(() => getTutorcloudLaunchUrl())
      .finally(() => {
        pendingLaunchUrl = null;
      });
  }
  return pendingLaunchUrl;
}

export function tutorcloudOpenErrorMessage(error) {
  const message = error?.message;
  if (typeof message === "string" && message.trim()) {
    return message.trim();
  }
  return "Could not open TutorCloud. Please try again.";
}

export function alertTutorcloudOpenError(error) {
  if (typeof window === "undefined" || typeof window.alert !== "function") {
    return;
  }
  window.alert(tutorcloudOpenErrorMessage(error));
}

/**
 * Open TutorCloud for the current teacher.
 * A handoff flag skips launch. Otherwise an entitled click mints the existing
 * JWT return URL. allowLaunch false keeps a direct billing URL (launch is 403).
 * An unauthorized launch does not fall back to a raw TutorCloud URL.
 */
export async function openTutorcloudDestination(
  { path, allowLaunch = true } = {},
  openUrl = openExternalTeacherUrl,
) {
  const destination = resolveDestinationUrl(path);
  if (!allowLaunch || hasTutorcloudHandoff()) {
    openUrl(destination);
    return { launched: false };
  }

  const data = await loadLaunchUrl();
  const launchUrl = String(data?.url || "").trim();
  if (!launchUrl) {
    throw new Error("TutorCloud is not available right now.");
  }
  // Set before navigation so the flag survives the page unload.
  markTutorcloudHandoff();
  openUrl(launchUrl);
  return { launched: true };
}

/** SSO handoff from iTEMA session → TutorCloud (one-time launch code). */
export async function openTutorcloudWithLaunch(nextPath = "/teacher/dashboard") {
  const data = await getTutorcloudLaunchUrl();
  const launchUrl = String(data?.url || "").trim();
  if (!launchUrl) {
    throw new Error("TutorCloud is not available right now.");
  }
  openExternalTeacherUrl(appendTutorcloudLaunchNext(launchUrl, nextPath));
}

export async function openTutorcloudBilling(tcBillingUrl, openUrl = openExternalTeacherUrl) {
  // Not-entitled billing stays a direct URL. Launch returns 403 for that teacher.
  return openTutorcloudDestination(
    { path: resolveTutorcloudBillingUrl(tcBillingUrl), allowLaunch: false },
    openUrl,
  );
}

export function openExternalTeacherUrl(url) {
  const target = String(url || "").trim();
  if (target) {
    window.location.assign(target);
  }
}

export function getTutorcloudTeacherDashboardUrl() {
  return joinTutorcloudUrl(getTutorcloudFrontendBaseUrl(), "/teacher/dashboard");
}

export function getTutorcloudTeacherSettingsUrl() {
  return joinTutorcloudUrl(
    getTutorcloudFrontendBaseUrl(),
    trimEnv(process.env.REACT_APP_TUTORCLOUD_TEACHER_SETTINGS_PATH, "/teacher/settings"),
  );
}

export function getTutorcloudStudentProfilesUrl() {
  return joinTutorcloudUrl(
    getTutorcloudFrontendBaseUrl(),
    trimEnv(
      process.env.REACT_APP_TUTORCLOUD_TEACHER_STUDENT_PROFILES_PATH,
      "/teacher/student-profiles",
    ),
  );
}

export function resolveQuickStartExternalUrl(linkKey, apiLinks = {}) {
  if (linkKey === "settings") {
    const fromApi = String(apiLinks.settings || "").trim();
    if (fromApi) {
      return isAbsoluteHttpUrl(fromApi)
        ? fromApi
        : joinTutorcloudUrl(getTutorcloudFrontendBaseUrl(), fromApi);
    }
    return getTutorcloudTeacherSettingsUrl();
  }
  if (linkKey === "student-profiles") {
    const fromApi = String(apiLinks.studentProfiles || "").trim();
    if (fromApi) {
      return isAbsoluteHttpUrl(fromApi)
        ? fromApi
        : joinTutorcloudUrl(getTutorcloudFrontendBaseUrl(), fromApi);
    }
    return getTutorcloudStudentProfilesUrl();
  }
  return "";
}
